import type { FC } from "react";

import Carousel from "@/design-system/carousel";
import { FetchGet } from "@/lib/fetch-helper";
import { useViewportInfoContext } from "@/src/contexts";
import { useQueryCustomNoSuspense } from "@/src/query-client-overrides";
import type { QuerySmMax, QueryXsMax } from "@/styles/tokens.stylex";
import type { IButton } from "@Components/Button";
import { Button } from "@Components/Button";
import { Loading } from "@Components/Loading";
import { urlFormat } from "@Components/Modules/Helpers";
import { Device } from "@Components/Modules/ViewportResize";
import { Brand } from "@Routes/BrandLinks";
import * as stylex from "@stylexjs/stylex";

import type { IBrandIdFollowResult } from "./BrandsByCategory";
import type { ILogoTileWithCounter } from "./LogoTileWithCounter";
import { LogoTileWithCounter } from "./LogoTileWithCounter";

export interface ISuggestedBrands {
    title: string;
    allBrandsButton: IButton;
    onButtonClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;
    onItemChange: (brand: IBrandIdFollowResult, result: boolean) => void;
    itemToUpdate: IBrandIdFollowResult;
    initialData: ILogoTileWithCounter[];
    clientId?: string;
}

export const SuggestedBrands: FC<ISuggestedBrands> = (props) => {
    const { device } = useViewportInfoContext();
    const onAllBrandsClick = (event: React.MouseEvent<HTMLAnchorElement>): void => {
        if (props.onButtonClick) {
            props.onButtonClick(event);
        }
    };

    const { data, isLoading } = useQueryCustomNoSuspense({
        queryKey: ["suggested-brands"],
        queryFn: () => FetchGet<ILogoTileWithCounter[]>(urlFormat(Brand, { action: "SuggestedBrands" })),
        initialData: props.initialData,
    });

    if (isLoading) {
        return <Loading />;
    }

    if (!(data?.length > 0)) {
        return null;
    }

    const numberOfItems = device === Device.Mobile ? data.length : device === Device.Tablet ? 3 : 5;
    const brands = data
        .slice(0, numberOfItems)
        .map((currentValue, index) => (
            <LogoTileWithCounter key={currentValue.id || index} {...currentValue} type="brands" />
        ));

    return (
        <section id={props.clientId}>
            <header>
                <h2>{props.title}</h2>
            </header>
            {device === Device.Mobile ? (
                <Carousel
                    itemsToShow={2.5}
                    showArrows={false}
                    styleX={styles.carousel}
                    itemStyleX={styles.item}
                >
                    {brands}
                </Carousel>
            ) : (
                <div {...stylex.props(styles.wrapper)}>{brands}</div>
            )}

            <Button {...props.allBrandsButton} {...stylex.props(styles.button)} onClick={onAllBrandsClick} />
        </section>
    );
};

const xsMax: QueryXsMax = "@media (max-width: 759px)";
const smMax: QuerySmMax = "@media (max-width: 989px)";

const styles = stylex.create({
    carousel: {
        gap: 0,
        marginBlock: "0",
        marginInline: "-10px",
        width: "auto",
    },

    item: {
        paddingBlock: "0",
        paddingInline: "10px",
    },
    button: {
        display: "block",
        marginBottom: 0,
        marginLeft: "auto",
        marginRight: "auto",
        marginTop: 30,
        width: {
            default: "calc(100% / 3)",
            [xsMax]: "60%",
        },
    },
    wrapper: {
        gap: "1rem",
        display: "grid",
        gridTemplateColumns: {
            default: "repeat(5, minmax(100px, 1fr))",
            [smMax]: "repeat(3, minmax(100px, 1fr))",
        },
    },
});
